import { apiRequest } from '../../lib/api-client.js';

export interface Server {
  id: number;
  name: string;
  url: string;
  key_label: string;
  status: 'active' | 'inactive';
  last_health_check_at: string | null;
  last_health_snapshot: HealthSnapshot | null;
  created_at: string;
}

export interface HealthSnapshot {
  ok: boolean;
  api_version: string;
  timestamp: string;
  counters: {
    contas_total: number;
    contas_ativas: number;
    empresas_total: number;
    empresas_ativas: number;
    usuarios_ativos: number;
  };
}

// ─── Domínio MetricsFarma: Conta → Empresas + Usuários ──────────────────────

export interface Conta {
  id: string;
  nome: string;
  email: string;
  telefone: string | null;
  plan: string;
  status: 'active' | 'trial' | 'suspended' | 'cancelled';
  user_id: string | null;
  created_at: string;
  total_empresas: number;
}

export interface Empresa {
  id: string;
  nome: string;
  slug: string;
  cnpj: string | null;
  razao_social: string | null;
  api_key: string;
  tracking_short_code: string;
  status: 'active' | 'suspended';
  created_at: string;
}

export interface ContaDetail {
  id: string;
  nome: string;
  email: string;
  telefone: string | null;
  plan: string;
  status: 'active' | 'trial' | 'suspended' | 'cancelled';
  user_id: string | null;
  created_at: string;
  empresas: Empresa[];
}

export interface Usuario {
  id: string;
  email: string;
  nome: string | null;
  profile: 'super_admin' | 'admin' | 'cliente';
  status: 'active' | 'inactive' | 'banned';
  last_login_at: string | null;
  created_at: string;
}

export interface CreateServerInput {
  name: string;
  url: string;
  key: string;
  key_label: string;
}

export interface PatchServerInput {
  name?: string;
  url?: string;
  key?: string;
  key_label?: string;
  status?: 'active' | 'inactive';
}

export interface CreateContaInput {
  nome: string;
  email: string;
  telefone?: string | null;
  plan?: 'starter' | 'pro' | 'enterprise';
  status?: 'active' | 'trial' | 'suspended' | 'cancelled';
}

export interface PatchContaInput {
  nome?: string;
  email?: string;
  telefone?: string | null;
  plan?: 'starter' | 'pro' | 'enterprise';
  status?: 'active' | 'trial' | 'suspended' | 'cancelled';
}

export interface CreateEmpresaInput {
  nome: string;
  cnpj?: string | null;
  razao_social?: string | null;
}

export interface CreateAdminInput {
  email: string;
  nome?: string;
  profile?: 'admin' | 'super_admin';
}

export interface UpdateAdminInput {
  email?: string;
  nome?: string;
  profile?: 'admin' | 'super_admin';
}

export const serversApi = {
  list: () => apiRequest<Server[]>('/api/servers'),
  create: (body: CreateServerInput) =>
    apiRequest<Server>('/api/servers', { method: 'POST', body }),
  patch: (id: number, body: PatchServerInput) =>
    apiRequest<Server>(`/api/servers/${id}`, { method: 'PATCH', body }),
  delete: (id: number) => apiRequest<void>(`/api/servers/${id}`, { method: 'DELETE' }),
  health: (id: number) =>
    apiRequest<{ status: number } & HealthSnapshot>(`/api/servers/${id}/health`),

  // ─── Contas ───
  contas: (id: number) => apiRequest<Conta[]>(`/api/servers/${id}/contas`),
  contaDetail: (id: number, cid: string) =>
    apiRequest<ContaDetail>(`/api/servers/${id}/contas/${cid}`),
  createConta: (id: number, body: CreateContaInput) =>
    apiRequest<{ conta: Omit<Conta, 'total_empresas'>; tempPassword: string }>(
      `/api/servers/${id}/contas`,
      { method: 'POST', body },
    ),
  patchConta: (id: number, cid: string, body: PatchContaInput) =>
    apiRequest<Conta>(`/api/servers/${id}/contas/${cid}`, { method: 'PATCH', body }),
  resetContaPassword: (id: number, cid: string, password?: string) =>
    apiRequest<{ tempPassword: string }>(`/api/servers/${id}/contas/${cid}/reset-password`, {
      method: 'POST',
      body: password ? { password } : {},
    }),

  // ─── Empresas ───
  empresas: (id: number, cid: string) =>
    apiRequest<Empresa[]>(`/api/servers/${id}/contas/${cid}/empresas`),
  createEmpresa: (id: number, cid: string, body: CreateEmpresaInput) =>
    apiRequest<Empresa>(`/api/servers/${id}/contas/${cid}/empresas`, { method: 'POST', body }),

  // ─── Usuários ───
  contaUsuarios: (id: number, cid: string) =>
    apiRequest<Usuario[]>(`/api/servers/${id}/contas/${cid}/usuarios`),
  resetUsuarioPassword: (id: number, cid: string, uid: string, password?: string) =>
    apiRequest<{ tempPassword: string }>(
      `/api/servers/${id}/contas/${cid}/usuarios/${uid}/reset-password`,
      { method: 'POST', body: password ? { password } : {} },
    ),
  patchUsuario: (id: number, cid: string, uid: string, status: 'active' | 'inactive') =>
    apiRequest<{ id: string; status: 'active' | 'inactive' }>(
      `/api/servers/${id}/contas/${cid}/usuarios/${uid}`,
      { method: 'PATCH', body: { status } },
    ),

  // ─── Usuários ADMIN globais (profile admin/super_admin) ───
  usuarios: (id: number) => apiRequest<Usuario[]>(`/api/servers/${id}/usuarios`),
  createUsuario: (id: number, body: CreateAdminInput) =>
    apiRequest<{
      id: string;
      email: string;
      nome: string | null;
      profile: 'admin' | 'super_admin';
      tempPassword: string;
    }>(`/api/servers/${id}/usuarios`, { method: 'POST', body }),
  patchUsuarioGlobal: (id: number, uid: string, body: UpdateAdminInput) =>
    apiRequest<{ id: string; email: string; nome: string | null; profile: string }>(
      `/api/servers/${id}/usuarios/${uid}`,
      { method: 'PATCH', body },
    ),
  deleteUsuario: (id: number, uid: string) =>
    apiRequest<void>(`/api/servers/${id}/usuarios/${uid}`, { method: 'DELETE' }),
  resetUsuarioPasswordGlobal: (id: number, uid: string, password?: string) =>
    apiRequest<{ tempPassword: string }>(`/api/servers/${id}/usuarios/${uid}/reset-password`, {
      method: 'POST',
      body: password ? { password } : {},
    }),
};
